import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { useState, useEffect } from "react";
import { PhoneInput } from "@/components/ui/phone-input";
import { ShippingOptionsResponse, Customer } from "@/api.types";
import { createOrderSchema, CreateOrderSchema } from "@/api.schemas";
import { PlusCircle, Trash2 } from "lucide-react";
import { useCreateOrder } from "@/api/orders";
import { useGetShippingOptions } from "@/api/pricing-options";
import { CustomerSearch } from "@/features/customers/customer-search";

interface PricingOption {
  speedType: string;
  estimatedDeliveryTime: { minHours: number; maxHours: number };
  price: number;
}

type PricingOptionMetadata = Pick<
  ShippingOptionsResponse,
  | "pickupServiceArea"
  | "deliveryServiceArea"
  | "totalWeight"
  | "totalDimensionalWeight"
  | "chargeableWeight"
>;

export function CreateOrderForm({ onSuccess }: { onSuccess?: () => void }) {
  const [pricingOptions, setPricingOptions] = useState<PricingOption[]>([]);
  const [pricingOptionMetadata, setPricingOptionMetadata] =
    useState<PricingOptionMetadata | null>(null);
  const [shippingOptionsError, setShippingOptionsError] = useState<
    string | null
  >(null);
  const [selectedCustomer, setSelectedCustomer] = useState<
    Customer | undefined
  >(undefined);

  const form = useForm<CreateOrderSchema>({
    resolver: zodResolver(createOrderSchema),
    defaultValues: {
      type: "STANDARD",
      status: "PENDING",
      recipientName: "",
      recipientEmail: "",
      recipientPhone: "",
      senderName: "",
      senderEmail: "",
      senderPhone: "",
      customerType: "INDIVIDUAL",
      placerCustomerId: "",
      // Initialize pickup address
      pickupAddress: {
        line1: "",
        line2: "",
        city: "",
        province: "",
        postalCode: "",
        country: "",
        location: {
          lat: 0,
          lng: 0,
        },
      },
      // Initialize delivery address
      deliveryAddress: {
        line1: "",
        line2: "",
        city: "",
        province: "",
        postalCode: "",
        country: "",
        location: {
          lat: 0,
          lng: 0,
        },
      },
      parcelData: [
        {
          weight: 2,
          dimensions: { length: 30, width: 30, height: 30 },
          isFragile: false,
          requiresCooling: false,
        },
      ],
      pickupNotes: "",
      deliveryNotes: "",
    },
  });

  // Set first pricing option when options become available
  useEffect(() => {
    if (pricingOptions.length > 0) {
      form.setValue(
        "type",
        pricingOptions[0].speedType as
          | "STANDARD"
          | "EXPRESS"
          | "SCHEDULED"
          | "SAME_DAY"
      );
    }
  }, [pricingOptions, form]);

  const shippingOptionsMutation = useGetShippingOptions();

  // Replace fetchPricingOptions with this function
  const fetchPricingOptions = async () => {
    const formData = form.getValues();

    // Check if all required fields are filled
    if (
      !formData.pickupAddress?.location?.lat ||
      !formData.pickupAddress?.location?.lng ||
      !formData.pickupAddress?.postalCode ||
      !formData.deliveryAddress?.location?.lat ||
      !formData.deliveryAddress?.location?.lng ||
      !formData.deliveryAddress?.postalCode ||
      !formData.parcelData?.length
    ) {
      setPricingOptions([]);
      setPricingOptionMetadata(null);
      return;
    }

    try {
      const response = await shippingOptionsMutation.mutateAsync({
        pickupAddress: {
          location: formData.pickupAddress.location,
          postalCode: formData.pickupAddress.postalCode,
        },
        deliveryAddress: {
          location: formData.deliveryAddress.location,
          postalCode: formData.deliveryAddress.postalCode,
        },
        parcels: formData.parcelData.map((parcel) => ({
          weight: parcel.weight ?? 0,
          dimensions: parcel.dimensions
            ? {
                length: parcel.dimensions.length ?? 0,
                width: parcel.dimensions.width ?? 0,
                height: parcel.dimensions.height ?? 0,
              }
            : undefined,
        })),
        specialHandlingTypes: formData.parcelData.flatMap((parcel) => {
          const types = [];
          if (parcel.isFragile) types.push("FRAGILE");
          if (parcel.requiresCooling) types.push("COOLING");
          return types;
        }),
        customerTierId: selectedCustomer?.tier?.id,
      });

      const { shippingOptions, reason, ...metadata } = response;
      setPricingOptions(shippingOptions);
      setPricingOptionMetadata(metadata as PricingOptionMetadata);
      setShippingOptionsError(reason ?? null);
    } catch (error) {
      console.error("Error fetching pricing options:", error);
      setPricingOptions([]);
      setPricingOptionMetadata(null);
      setShippingOptionsError("Unable to calculate shipping options");
      form.setValue("type", "STANDARD");
    }
  };

  // Update the useEffect to debounce the API calls
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (
        name?.includes("pickupAddress") ||
        name?.includes("deliveryAddress") ||
        name?.includes("parcelData") ||
        name === "customerType" ||
        name === "placerCustomerId"
      ) {
        // Add debounce to prevent too many API calls
        const timeoutId = setTimeout(() => {
          void fetchPricingOptions();
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  const createOrderMutation = useCreateOrder({
    onSuccess: () => {
      form.reset();
      onSuccess?.();
    },
  });

  const onSubmit = form.handleSubmit(
    (values) => {
      try {
        // Find the selected shipping option
        const selectedOption = pricingOptions.find(
          (option) => option.speedType === values.type
        );

        if (!selectedOption) {
          console.error("No shipping option selected");
          return;
        }

        // Format parcel data
        const formattedParcels = values.parcelData.map((parcel) => ({
          weight: Number(parcel.weight) || 0,
          dimensions: {
            length: Number(parcel.dimensions?.length) || 0,
            width: Number(parcel.dimensions?.width) || 0,
            height: Number(parcel.dimensions?.height) || 0,
          },
          isFragile: Boolean(parcel.isFragile),
          requiresCooling: Boolean(parcel.requiresCooling),
        }));

        // Submit the form with depot IDs from the pricing options
        const submitData = {
          ...values,
          pickupDepotId:
            pricingOptionMetadata?.pickupServiceArea?.depotId ?? null,
          deliveryDepotId:
            pricingOptionMetadata?.deliveryServiceArea?.depotId ?? null,
          // Set emails to null if they're empty strings
          senderEmail: values.senderEmail?.trim() ?? null,
          recipientEmail: values.recipientEmail?.trim() ?? null,
          // Include formatted parcels
          parcels: formattedParcels,
        };

        console.log("Submitting order with data:", submitData);
        createOrderMutation.mutate(submitData);
      } catch (error) {
        console.error("Error preparing order data:", error);
        // Add error handling/user feedback here
      }
    },
    (errors) => {
      console.error("Form validation errors:", errors);
    }
  );

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className="space-y-8 max-h-[80vh] overflow-y-scroll pl-1 pr-2"
      >
        {/* Add customer search field */}
        <FormField
          control={form.control}
          name="placerCustomerId"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Customer</FormLabel>
              <CustomerSearch
                value={field.value}
                onValueChange={(value) => field.onChange(value)}
                onCustomerSelect={setSelectedCustomer}
                placeholder="Select customer..."
              />
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Two-column layout for sender and recipient */}
        <div className="grid grid-cols-2 gap-8">
          {/* Sender Column */}
          <div className="space-y-8">
            <FormField
              control={form.control}
              name="senderName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sender Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Sender Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="senderPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sender Phone</FormLabel>
                  <FormControl>
                    <PhoneInput
                      placeholder="Sender Phone"
                      {...field}
                      value={field.value}
                      onChange={(value) => field.onChange(value || "")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="w-full">
              <FormLabel>Pickup Address</FormLabel>
              <div className="mt-2 relative z-50">
                <AutocompleteAddress
                  onAddressChange={(address) => {
                    if (address) {
                      form.setValue("pickupAddress", {
                        line1: address.addressComponents.line1,
                        line2: address.addressComponents.line2,
                        city: address.addressComponents.city,
                        province: address.addressComponents.state,
                        postalCode: address.addressComponents.postalCode || "",
                        country: address.addressComponents.country,
                        location: address.location,
                      });
                    }
                  }}
                  onLine2Change={(value) => {
                    const currentAddress = form.getValues("pickupAddress");
                    if (currentAddress) {
                      form.setValue(
                        "pickupAddress",
                        {
                          ...currentAddress,
                          line2: value,
                        },
                        {
                          shouldValidate: false,
                        }
                      );
                    }
                  }}
                  placeholder="Search pickup address"
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="pickupNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pickup Notes</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Add any special instructions for pickup"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Recipient Column */}
          <div className="space-y-8">
            <FormField
              control={form.control}
              name="recipientName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Recipient Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Recipient Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="recipientPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Recipient Phone</FormLabel>
                  <FormControl>
                    <PhoneInput
                      placeholder="Recipient Phone"
                      {...field}
                      value={field.value}
                      onChange={(value) => field.onChange(value || "")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="w-full">
              <FormLabel>Delivery Address</FormLabel>
              <div className="mt-2 relative z-50">
                <AutocompleteAddress
                  onAddressChange={(address) => {
                    if (address) {
                      form.setValue("deliveryAddress", {
                        line1: address.addressComponents.line1,
                        line2: address.addressComponents.line2,
                        city: address.addressComponents.city,
                        province: address.addressComponents.state,
                        postalCode: address.addressComponents.postalCode,
                        country: address.addressComponents.country,
                        location: address.location,
                      });
                    }
                  }}
                  onLine2Change={(value) => {
                    const currentAddress = form.getValues("deliveryAddress");
                    if (currentAddress) {
                      form.setValue(
                        "deliveryAddress",
                        {
                          ...currentAddress,
                          line2: value,
                        },
                        {
                          shouldValidate: false,
                        }
                      );
                    }
                  }}
                  placeholder="Search delivery address"
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="deliveryNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Delivery Notes</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Add any special instructions for delivery"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Parcels</CardTitle>
            </div>
            <div className="flex items-center space-x-2">
              {/* <Button variant="outline" size="icon">
                <Printer className="h-4 w-4" />
              </Button>
              <Button variant="outline">Cancel</Button>
              <Button>Save</Button> */}
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dimensions (L×W×H cm)</TableHead>
                  <TableHead>Weight (kg)</TableHead>
                  <TableHead>Special Handling</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.watch("parcelData").map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Input
                          type="number"
                          placeholder="L"
                          {...form.register(
                            `parcelData.${index}.dimensions.length`
                          )}
                          className="w-20"
                        />
                        <Input
                          type="number"
                          placeholder="W"
                          {...form.register(
                            `parcelData.${index}.dimensions.width`
                          )}
                          className="w-20"
                        />
                        <Input
                          type="number"
                          placeholder="H"
                          {...form.register(
                            `parcelData.${index}.dimensions.height`
                          )}
                          className="w-20"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        placeholder="Weight"
                        {...form.register(`parcelData.${index}.weight`)}
                        className="w-24"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Checkbox
                          checked={form.watch(`parcelData.${index}.isFragile`)}
                          onCheckedChange={(checked) =>
                            form.setValue(
                              `parcelData.${index}.isFragile`,
                              !!checked
                            )
                          }
                        />
                        <span className="text-sm">Fragile</span>
                        <Checkbox
                          checked={form.watch(
                            `parcelData.${index}.requiresCooling`
                          )}
                          onCheckedChange={(checked) =>
                            form.setValue(
                              `parcelData.${index}.requiresCooling`,
                              !!checked
                            )
                          }
                        />
                        <span className="text-sm">Cooling</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {form.watch("parcelData").length > 1 && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            const parcels = form.getValues("parcelData");
                            form.setValue(
                              "parcelData",
                              parcels.filter((_, i) => i !== index)
                            );
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
          <CardFooter className="justify-center border-t p-4">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1"
              onClick={() => {
                const parcels = form.getValues("parcelData");
                form.setValue("parcelData", [
                  ...parcels,
                  {
                    weight: 2,
                    dimensions: { length: 30, width: 30, height: 30 },
                    isFragile: false,
                    requiresCooling: false,
                  },
                ]);
              }}
            >
              <PlusCircle className="h-3.5 w-3.5" />
              Add Parcel
            </Button>
          </CardFooter>
        </Card>

        {/* Pricing Options Display */}
        {(pricingOptions || shippingOptionsError) && (
          <Card>
            <CardHeader>
              <CardTitle>Delivery Options</CardTitle>
            </CardHeader>
            <CardContent>
              {pricingOptions.length > 0 ? (
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <div className="space-y-4">
                      {pricingOptions.map((option) => (
                        <div
                          key={option.speedType}
                          className={`p-6 rounded-xl max-w-[500px] border-2 cursor-pointer transition-all ${
                            field.value === option.speedType
                              ? "border-blue-500 bg-blue-50/50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() =>
                            field.onChange(
                              option.speedType as
                                | "STANDARD"
                                | "EXPRESS"
                                | "SCHEDULED"
                                | "SAME_DAY"
                            )
                          }
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium text-lg">
                                {option.speedType.replace("_", " ")}
                              </h4>
                              <p className="text-sm text-gray-500 mt-1">
                                Estimated Delivery Time:{" "}
                                {typeof option.estimatedDeliveryTime ===
                                "object"
                                  ? `${option.estimatedDeliveryTime.minHours}-${option.estimatedDeliveryTime.maxHours} hours`
                                  : option.estimatedDeliveryTime}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">
                                ${option.price.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                />
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500">No delivery options available</p>
                  {shippingOptionsError && (
                    <p className="text-sm text-amber-600 mt-2">
                      {shippingOptionsError}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Button type="submit">Create Order</Button>
      </form>
    </Form>
  );
}
